import { fetchConfig, isFreeGiftLine } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";

class FreeGiftController {
  #reconciling = false;
  #queued = false;
  #addFailed = false;
  #thresholdCents = 0;

  constructor() {
    this.#setupThreshold();
    if (!this.#config.enabled || !this.#variantId || this.#thresholdCents <= 0) return;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#reconcile();
  }

  get #config() {
    return window.FoxTheme?.freeGift || {};
  }

  get #variantId() {
    return Number(this.#config.variantId) || 0;
  }

  get #wrapVariantId() {
    return Number(this.#config.wrapVariantId) || 0;
  }

  #setupThreshold() {
    const minimumAmount = parseFloat(this.#config.minimumAmount);
    if (isNaN(minimumAmount) || minimumAmount <= 0) {
      this.#thresholdCents = 0;
      return;
    }

    const isSingleCurrency = this.#config.isSingleCurrency === true || this.#config.isSingleCurrency === "true";
    const shopCurrency = this.#config.shopCurrency;
    const currentCurrency = this.#config.currentCurrency;

    if (isSingleCurrency && currentCurrency && shopCurrency && currentCurrency !== shopCurrency) {
      const rate = parseFloat(window.Shopify?.currency?.rate);
      if (!isNaN(rate) && rate > 0) {
        this.#thresholdCents = Math.round(minimumAmount * rate * 100);
        return;
      }
    }

    this.#thresholdCents = Math.round(minimumAmount * 100);
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === "free-gift") return;
    this.#addFailed = false;
    this.#reconcile(event.detail?.resource);
  };

  async #reconcile(preloadedCart) {
    if (this.#reconciling) {
      this.#queued = true;
      return;
    }

    this.#reconciling = true;
    try {
      do {
        this.#queued = false;
        await this.#reconcileOnce(preloadedCart);
        preloadedCart = undefined;
      } while (this.#queued);
    } finally {
      this.#reconciling = false;
    }
  }

  async #reconcileOnce(preloadedCart) {
    const cart = await this.#resolveCart(preloadedCart);
    if (!cart?.items) return;

    const giftLines = cart.items.filter((item) => this.#isConfiguredGiftLine(item));
    const staleGiftLines = cart.items.filter((item) => isFreeGiftLine(item) && !this.#isConfiguredGiftLine(item));
    const qualified = this.#qualifyingTotal(cart) >= this.#thresholdCents;

    if (!qualified) {
      await this.#removeLines([...giftLines, ...staleGiftLines]);
      return;
    }

    await this.#removeLines(staleGiftLines);

    if (giftLines.length === 0) {
      if (this.#addFailed) return;
      await this.#addGift();
      return;
    }

    const [keep, ...extras] = giftLines;
    if (keep.quantity !== 1) {
      await this.#changeLine(keep.key, 1);
    }
    await this.#removeLines(extras);
  }

  #isConfiguredGiftLine(item) {
    return isFreeGiftLine(item) && Number(item.variant_id) === this.#variantId;
  }

  #qualifyingTotal(cart) {
    const wrapVariantId = this.#wrapVariantId;
    return (cart.items || []).reduce((sum, item) => {
      if (isFreeGiftLine(item)) return sum;
      if (wrapVariantId && Number(item.variant_id) === wrapVariantId) return sum;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
  }

  async #resolveCart(preloadedCart) {
    if (preloadedCart && typeof preloadedCart === "object" && Array.isArray(preloadedCart.items)) {
      return preloadedCart;
    }
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  #getSectionPayload() {
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return {
      sections: ids.join(","),
      sections_url: window.location.pathname,
    };
  }

  async #addGift() {
    const body = JSON.stringify({
      items: [
        {
          id: this.#variantId,
          quantity: 1,
          properties: { _free_gift: "true" },
        },
      ],
      ...this.#getSectionPayload(),
    });

    const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    const parsed = await res.json();

    if (parsed.status && parsed.message && !Array.isArray(parsed.items)) {
      this.#addFailed = true;
      console.error(parsed.message);
      return;
    }

    if (parsed.status === 422 && !parsed.sections) {
      this.#addFailed = true;
      return;
    }

    await this.#applyCartJsonResponse(parsed);
  }

  async #changeLine(key, quantity) {
    const body = JSON.stringify({
      id: key,
      quantity,
      ...this.#getSectionPayload(),
    });
    const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
    const parsed = await res.json();
    await this.#applyCartJsonResponse(parsed);
  }

  async #removeLines(items) {
    for (const item of items) {
      if (!item?.key) continue;
      await this.#changeLine(item.key, 0);
    }
  }

  async #applyCartJsonResponse(parsed) {
    if (!parsed?.sections) {
      const cart = parsed?.item_count != null ? parsed : await this.#resolveCart();
      const itemCount = Number(cart?.item_count) || 0;
      this.#dispatchCartUpdate(cart, itemCount, {});
      return;
    }

    const sections = parsed.sections;
    let itemCount = Number(parsed.item_count) || 0;

    for (const sectionId of Object.keys(sections)) {
      await morphSection(sectionId, sections[sectionId]);
      const doc = new DOMParser().parseFromString(sections[sectionId], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) {
        itemCount = parseInt(countEl.textContent, 10) || itemCount;
      }
    }

    this.#dispatchCartUpdate({}, itemCount, sections);
  }

  #dispatchCartUpdate(resource, itemCount, sections) {
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const data = {
      itemCount,
      source: "free-gift",
      sections,
    };

    if (cartItemsComponents.length === 0) {
      document.dispatchEvent(new CartUpdateEvent(resource, "free-gift", data));
      return;
    }

    cartItemsComponents.forEach((comp) => {
      const sid = comp instanceof HTMLElement ? comp.dataset.sectionId : "";
      if (sid && sections[sid]) {
        comp.dispatchEvent(new CartUpdateEvent(resource, sid, data));
      } else {
        comp.dispatchEvent(new CartUpdateEvent(resource, sid || "free-gift", data));
      }
    });
  }
}

new FreeGiftController();
